import gql from 'graphql-tag';
import * as React from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { SaveForLaterMutation, SaveForLaterMutationVariables } from './__generated__/SaveForLaterMutation';
import { SaveVenueMutation, SaveVenueMutationVariables } from './__generated__/SaveVenueMutation';
import { VenueInviteQuery, VenueInviteQueryVariables } from './__generated__/VenueInviteQuery';
import { Onboarding } from './Onboarding';
import { AuthContext } from '../../AuthService';
import { BRAINTREE_STEPS, FREEDOMPAY_STEPS } from './Steps';
import { useApolloErrorWrapper } from '../../hooks/useApolloErrorWrapper';
import { useLogin } from '../../hooks/useLogin';

interface OnboardingProps extends RouteComponentProps<{ uuid: string }> {}

const VENUE_INVITE_QUERY = gql`
  query VenueInviteQuery($uuid: String!) {
    venueInvite(uuid: $uuid) {
      email
      firstName
      lastName
      userId
      savedData
      paymentProcessor
    }
  }
`;

const SAVE_VENUE_FOR_LATER = gql`
  mutation SaveForLaterMutation($uuid: String!, $data: JSONObject!) {
    saveForLater(uuid: $uuid, data: $data)
  }
`;

const SAVE_VENUE = gql`
  mutation SaveVenueMutation($uuid: String!, $data: VenueInviteInput!, $image: Upload) {
    createVenue(uuid: $uuid, data: $data, image: $image) {
      id
    }
  }
`;

const getPaymentProcessor = (data: VenueInviteQuery) => {
  return data.venueInvite.paymentProcessor === 'braintree' ? BRAINTREE_STEPS : FREEDOMPAY_STEPS;
};

export const OnboardingContainer: React.FC<OnboardingProps> = (props) => {
  const { onError, error, clearError } = useApolloErrorWrapper();
  const [login] = useLogin();
  const [createVenue, createVenueInvite] = useMutation<SaveVenueMutation, SaveVenueMutationVariables>(SAVE_VENUE, {
    onError,
  });
  const [saveForLater, saveForLaterState] = useMutation<SaveForLaterMutation, SaveForLaterMutationVariables>(
    SAVE_VENUE_FOR_LATER,
    {
      onError,
    }
  );
  const { data, loading } = useQuery<VenueInviteQuery, VenueInviteQueryVariables>(VENUE_INVITE_QUERY, {
    variables: {
      uuid: props.match.params.uuid,
    },
    onError,
  });

  const auth = React.useContext(AuthContext);

  return (
    <Onboarding
      loading={loading || saveForLaterState.loading || createVenueInvite.loading}
      invite={data ? data.venueInvite : undefined}
      steps={data && data.venueInvite ? getPaymentProcessor(data) : undefined}
      onSubmit={createVenue}
      onSaveForLater={saveForLater}
      uuid={props.match.params.uuid}
      auth={auth}
      venueId={createVenueInvite?.data?.createVenue.id}
      error={error}
      clearError={clearError}
      login={login}
      {...props}
    />
  );
};
